import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { IMaskInput } from 'react-imask'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { apiFetch } from '../../lib/api'
import { brl } from '../../lib/format'
import { useAuth } from '../../state/auth'
import { ConfirmModal } from '../ConfirmModal'

type Employee = {
  id: string
  fullName: string
  cpfMasked: string
  roleTitle: string
  baseSalary: number
  admissionDateBr: string
}

type Company = { id: string; tradeName: string; legalName: string; cnpjMasked: string }

type TermItem = { label: string; value: number; kind: 'provento' | 'desconto' }
type TermResult = { gross: number; discounts: number; net: number; items: TermItem[] }
type Calc = {
  id: string
  createdAt: string
  employeeName: string
  employee?: Employee | null
  company?: { tradeName: string; cnpjMasked: string } | null
  input?: any
  result: TermResult
}

const schema = z.object({
  companyId: z.string().min(1, 'Selecione a empresa'),
  employeeId: z.string().min(1, 'Selecione um funcionário'),
  terminationDateBr: z.string().min(10, 'Informe a data (DD/MM/AAAA)'),
  terminationType: z.enum(['sem_justa_causa', 'pedido_demissao', 'justa_causa']),
  notice: z.enum(['indenizado', 'trabalhado', 'sem_aviso']),
  // exibição (preenchido automaticamente)
  baseSalary: z.string().optional(),

  // horas extras
  hoursExtraQty: z.coerce.number().min(0).optional(),
  hoursExtraPercent: z.coerce.number().min(0).optional(),

  // inclusões
  includeSaldoSalario: z.boolean().optional(),
  includeFeriasVencidas: z.boolean().optional(),
  includeFeriasProporcionais: z.boolean().optional(),
  includeDecimoTerceiro: z.boolean().optional(),
  includeDecimoTerceiroAnoAnterior: z.boolean().optional(),

  // descontos
  discountValeTransporte: z.string().optional(),
  discountAlimentacao: z.string().optional(),
  faltasDias: z.coerce.number().min(0).optional(),
  horasFaltas: z.coerce.number().min(0).optional(),
  outrosDescontos: z.string().optional()
})

type FormData = z.infer<typeof schema>

export function TerminationPage() {
  const { selectedCompanyId, setSelectedCompanyId } = useAuth()
  const qc = useQueryClient()
  const [deleteCalcConfirm, setDeleteCalcConfirm] = useState<Calc | null>(null)

  const companiesQ = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiFetch<{ companies: Company[] }>(`/companies`)
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyId: selectedCompanyId ?? '',
      terminationType: 'sem_justa_causa',
      notice: 'indenizado',
      hoursExtraQty: 0,
      hoursExtraPercent: 50,
      includeSaldoSalario: true,
      includeFeriasVencidas: false,
      includeFeriasProporcionais: true,
      includeDecimoTerceiro: true,
      includeDecimoTerceiroAnoAnterior: false,
      faltasDias: 0,
      horasFaltas: 0
    }
  })

  const calculationCompanyId = watch('companyId') || selectedCompanyId || ''
  const employeeId = watch('employeeId')

  useEffect(() => {
    if (selectedCompanyId) setValue('companyId', selectedCompanyId)
  }, [selectedCompanyId, setValue])

  useEffect(() => {
    const companies = companiesQ.data?.companies ?? []
    if (!calculationCompanyId && companies.length > 0) {
      setValue('companyId', companies[0]!.id)
      setSelectedCompanyId(companies[0]!.id)
    }
  }, [calculationCompanyId, companiesQ.data?.companies, setSelectedCompanyId, setValue])

  const employeesQ = useQuery({
    queryKey: ['employees', calculationCompanyId],
    enabled: !!calculationCompanyId,
    queryFn: () => apiFetch<{ employees: Employee[] }>(`/companies/${calculationCompanyId}/employees`)
  })

  const historyQ = useQuery({
    queryKey: ['term-history', calculationCompanyId],
    enabled: !!calculationCompanyId,
    queryFn: () => apiFetch<{ calculations: Calc[] }>(`/companies/${calculationCompanyId}/termination-calculations`)
  })

  const employees = employeesQ.data?.employees ?? []
  const selectedEmployee = useMemo(() => employees.find((e) => e.id === employeeId) ?? null, [employees, employeeId])

  useEffect(() => {
    if (selectedEmployee) {
      setValue('baseSalary', brl.format(selectedEmployee.baseSalary))
    } else {
      setValue('baseSalary', '')
    }
  }, [selectedEmployee, setValue])

  const selectedCompany = useMemo(() => {
    const list = companiesQ.data?.companies ?? []
    return list.find((c) => c.id === calculationCompanyId) ?? null
  }, [calculationCompanyId, companiesQ.data?.companies])

  const exportPdf = async (_title: string, calc: Calc) => {
    const doc = new jsPDF()
    const COLOR = [97, 110, 179] as const // #616eb3
    const margin = 14
    const pageWidth = doc.internal.pageSize.getWidth()
    let y = 14

    // Logo no topo
    try {
      const cached = (window as any).__ncc_logo_pdf_cache as string | undefined
      const img =
        cached ??
        ((window as any).__ncc_logo_pdf_cache = await fetch('/logo-pdf.png')
          .then((r) => r.blob())
          .then(
            (b) =>
              new Promise<string>((resolve) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result as string)
                reader.readAsDataURL(b)
              })
          ))
      const w = 55
      const h = 20
      doc.addImage(img, 'PNG', (pageWidth - w) / 2, y, w, h)
      y += h + 8
    } catch {
      // ignore
    }

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text('TERMO DE QUITAÇÃO TRABALHISTA', pageWidth / 2, y, { align: 'center' })
    y += 10

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.text(`Gerado em: ${new Date(calc.createdAt).toLocaleString('pt-BR')}`, margin, y)
    y += 8

    const emp = calc.company ?? selectedCompany
    const func = calc.employee ?? selectedEmployee

    const drawSectionTitle = (t: string) => {
      doc.setDrawColor(...COLOR)
      doc.setTextColor(20, 20, 20)
      doc.setFont('helvetica', 'bold')
      doc.text(t, margin, y)
      y += 2
      doc.setLineWidth(0.5)
      doc.line(margin, y, pageWidth - margin, y)
      y += 6
      doc.setFont('helvetica', 'normal')
    }

    drawSectionTitle('DADOS DO EMPREGADOR')
    doc.text(`Empresa: ${emp?.tradeName ?? ''}`, margin, y)
    doc.text(`CNPJ: ${emp?.cnpjMasked ?? ''}`, pageWidth / 2, y)
    y += 8

    drawSectionTitle('DADOS DO EMPREGADO')
    doc.text(`Nome: ${func?.fullName ?? calc.employeeName}`, margin, y)
    doc.text(`CPF: ${func?.cpfMasked ?? ''}`, pageWidth / 2, y)
    y += 6
    doc.text(`Cargo: ${func?.roleTitle ?? ''}`, margin, y)
    doc.text(`Admissão: ${func?.admissionDateBr ?? ''}`, pageWidth / 2, y)
    y += 6
    doc.text(`Demissão: ${calc.input?.terminationDateBr ?? watch('terminationDateBr') ?? ''}`, margin, y)
    doc.text(`Salário Base: ${func ? brl.format(func.baseSalary) : watch('baseSalary') ?? ''}`, pageWidth / 2, y)
    y += 10

    drawSectionTitle('DISCRIMINAÇÃO DOS VALORES')

    autoTable(doc, {
      startY: y,
      head: [['PROVENTOS / DESCONTOS', 'VALOR (R$)']],
      body: calc.result.items.map((i) => [i.label, brl.format(i.value)]),
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: COLOR as any, textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } }
    })

    y = (doc as any).lastAutoTable.finalY + 8
    doc.setDrawColor(...COLOR)
    doc.setLineWidth(0.6)
    doc.rect(margin, y, pageWidth - margin * 2, 10)
    doc.setFont('helvetica', 'bold')
    doc.text(`VALOR LÍQUIDO A RECEBER: ${brl.format(calc.result.net)}`, pageWidth / 2, y + 7, { align: 'center' })
    y += 18

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.text(
      `Eu, ${func?.fullName ?? calc.employeeName}, declaro que recebi da empresa ${emp?.tradeName ?? ''}, inscrita no CNPJ ${emp?.cnpjMasked ?? ''}, o valor líquido de ${brl.format(calc.result.net)}, referente ao acerto de rescisão do meu contrato de trabalho, dando-lhe plena, geral e irrevogável quitação.`,
      margin,
      y,
      { maxWidth: pageWidth - margin * 2 }
    )
    y += 22

    // Assinaturas
    doc.setDrawColor(120, 120, 120)
    doc.setLineWidth(0.3)
    const lineW = (pageWidth - margin * 2 - 10) / 2
    const x1 = margin
    const x2 = margin + lineW + 10
    doc.line(x1, y, x1 + lineW, y)
    doc.line(x2, y, x2 + lineW, y)
    doc.setFontSize(9)
    doc.text('Assinatura da Empresa', x1 + lineW / 2, y + 5, { align: 'center' })
    doc.text('Assinatura do Funcionário', x2 + lineW / 2, y + 5, { align: 'center' })
    doc.setFontSize(8)
    doc.text(func?.fullName ?? calc.employeeName, x2 + lineW / 2, y + 10, { align: 'center' })

    doc.save(`rescisao_${(func?.fullName ?? calc.employeeName ?? 'funcionario').replace(/\s+/g, '_')}.pdf`)
  }

  const onSubmit = async (data: FormData) => {
    const companyId = data.companyId || calculationCompanyId
    if (!companyId) return
    const resp = await apiFetch<{ calculation: Calc }>(`/companies/${companyId}/termination-calculations`, {
      method: 'POST',
      body: JSON.stringify({
        employeeId: data.employeeId,
        terminationDateBr: data.terminationDateBr,
        terminationType: data.terminationType,
        notice: data.notice,
        hoursExtraQty: String(data.hoursExtraQty ?? 0),
        hoursExtraPercent: String(data.hoursExtraPercent ?? 0),
        includeSaldoSalario: !!data.includeSaldoSalario,
        includeFeriasVencidas: !!data.includeFeriasVencidas,
        includeFeriasProporcionais: !!data.includeFeriasProporcionais,
        includeDecimoTerceiro: !!data.includeDecimoTerceiro,
        includeDecimoTerceiroAnoAnterior: !!data.includeDecimoTerceiroAnoAnterior,
        discountValeTransporte: data.discountValeTransporte ?? '',
        discountAlimentacao: data.discountAlimentacao ?? '',
        faltasDias: String(data.faltasDias ?? 0),
        horasFaltas: String(data.horasFaltas ?? 0),
        outrosDescontos: data.outrosDescontos ?? ''
      })
    })
    toast.success('Cálculo salvo no histórico')
    await qc.invalidateQueries({ queryKey: ['term-history', companyId] })
    await exportPdf('Cálculo de Rescisão', resp.calculation)
  }

  const openDeleteHistoryConfirm = (calc: Calc) => setDeleteCalcConfirm(calc)

  const handleDeleteHistory = async () => {
    if (!calculationCompanyId || !deleteCalcConfirm) return
    const calc = deleteCalcConfirm
    setDeleteCalcConfirm(null)
    await apiFetch(`/companies/${calculationCompanyId}/termination-calculations/${calc.id}`, { method: 'DELETE' })
    toast.success('Removido do histórico')
    await qc.invalidateQueries({ queryKey: ['term-history', calculationCompanyId] })
  }

  if (!calculationCompanyId && !companiesQ.isLoading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-700">
        Cadastre ou selecione uma empresa para calcular rescisão.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ConfirmModal
        open={!!deleteCalcConfirm}
        title="Excluir do histórico"
        message="Excluir este PDF do histórico?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDeleteHistory}
        onCancel={() => setDeleteCalcConfirm(null)}
      />
      <div>
        <div className="text-xl font-semibold text-slate-900">Cálculo de Rescisão Trabalhista</div>
        <div className="mt-1 text-sm text-slate-500">Cálculo com detalhamento por verba e exportação em PDF.</div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-semibold text-slate-900">Novo cálculo</div>
          <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label className="text-sm font-medium text-slate-700">Empresa para o cálculo</label>
              <select
                className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                {...register('companyId')}
                onChange={(e) => {
                  const companyId = e.target.value
                  setValue('companyId', companyId)
                  setValue('employeeId', '')
                  setValue('baseSalary', '')
                  setSelectedCompanyId(companyId || null)
                }}
              >
                <option value="">Selecione a empresa</option>
                {(companiesQ.data?.companies ?? []).map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.tradeName}
                  </option>
                ))}
              </select>
              {errors.companyId && <div className="mt-1 text-xs text-red-600">{errors.companyId.message}</div>}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Funcionário</label>
              <select className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('employeeId')}>
                <option value="">Selecione</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.fullName}
                  </option>
                ))}
              </select>
              {errors.employeeId && <div className="mt-1 text-xs text-red-600">{errors.employeeId.message}</div>}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Salário base (preenchido automaticamente)</label>
                <input
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-900"
                  value={watch('baseSalary') ?? ''}
                  readOnly
                />
              </div>
              <div className="rounded-xl border border-[#616eb3]/20 bg-[#616eb3]/10 p-4 text-xs text-slate-700">
                <div className="font-semibold text-[#616eb3]">Regra de multa FGTS</div>
                <div className="mt-1">Sem justa causa = Multa 40% FGTS</div>
                <div>Pedido de demissão = Sem multa</div>
                <div>Justa causa = Sem multa</div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Data de desligamento</label>
                <IMaskInput
                  mask="00/00/0000"
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  onAccept={(v) => setValue('terminationDateBr', String(v))}
                />
                {errors.terminationDateBr && <div className="mt-1 text-xs text-red-600">{errors.terminationDateBr.message}</div>}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tipo de rescisão</label>
                <select className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('terminationType')}>
                  <option value="sem_justa_causa">Sem justa causa</option>
                  <option value="pedido_demissao">Pedido de demissão</option>
                  <option value="justa_causa">Justa causa</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Aviso prévio</label>
                <select className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('notice')}>
                  <option value="indenizado">Indenizado</option>
                  <option value="trabalhado">Trabalhado</option>
                  <option value="sem_aviso">Sem aviso</option>
                </select>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-900">Horas extras</div>
              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">Quantidade de Horas Extras</label>
                  <input type="number" min={0} step="0.5" className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('hoursExtraQty')} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Percentual Horas Extras (%)</label>
                  <input type="number" min={0} step="1" className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('hoursExtraPercent')} />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-900">Verbas a calcular</div>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1 h-4 w-4" {...register('includeSaldoSalario')} />
                  <span>
                    <span className="font-medium">Possui Saldo de Salário?</span>
                    <div className="text-xs text-slate-500">Se marcado, calcula saldo proporcional aos dias trabalhados no último mês.</div>
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1 h-4 w-4" {...register('includeFeriasVencidas')} />
                  <span>
                    <span className="font-medium">Possui Férias Vencidas?</span>
                    <div className="text-xs text-slate-500">Se marcado, o sistema calculará automaticamente baseado no período trabalhado e exibirá no documento final.</div>
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1 h-4 w-4" {...register('includeFeriasProporcionais')} />
                  <span>
                    <span className="font-medium">Calcular Férias Proporcionais?</span>
                    <div className="text-xs text-slate-500">Se marcado, calcula férias proporcionais + 1/3 constitucional do período incompleto.</div>
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1 h-4 w-4" {...register('includeDecimoTerceiro')} />
                  <span>
                    <span className="font-medium">Calcular 13º Proporcional?</span>
                    <div className="text-xs text-slate-500">Se marcado, calcula o 13º salário proporcional aos meses trabalhados no ano.</div>
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1 h-4 w-4" {...register('includeDecimoTerceiroAnoAnterior')} />
                  <span>
                    <span className="font-medium">13º do ano anterior não foi pago?</span>
                    <div className="text-xs text-slate-500">Marque para incluir os avos do ano anterior trabalhados antes da rescisão.</div>
                  </span>
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-900">Descontos</div>
              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">Vale Transporte (R$)</label>
                  <IMaskInput
                    mask={Number}
                    radix=","
                    thousandsSeparator="."
                    scale={2}
                    padFractionalZeros
                    normalizeZeros
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                    onAccept={(v) => setValue('discountValeTransporte', String(v))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Alimentação (R$)</label>
                  <IMaskInput
                    mask={Number}
                    radix=","
                    thousandsSeparator="."
                    scale={2}
                    padFractionalZeros
                    normalizeZeros
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                    onAccept={(v) => setValue('discountAlimentacao', String(v))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Faltas (dias)</label>
                  <input type="number" min={0} step="1" className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('faltasDias')} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Horas faltas</label>
                  <input type="number" min={0} step="0.5" className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm" {...register('horasFaltas')} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Outros Descontos (R$)</label>
                  <IMaskInput
                    mask={Number}
                    radix=","
                    thousandsSeparator="."
                    scale={2}
                    padFractionalZeros
                    normalizeZeros
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                    onAccept={(v) => setValue('outrosDescontos', String(v))}
                  />
                </div>
              </div>
            </div>

            <button
              disabled={isSubmitting}
              className="h-11 w-full rounded-xl bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              type="submit"
            >
              {isSubmitting ? 'Calculando…' : 'Calcular e exportar PDF'}
            </button>
          </form>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-semibold text-slate-900">Histórico</div>
          <div className="mt-4 space-y-2">
            {(historyQ.data?.calculations ?? []).map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{c.employeeName}</div>
                    <div className="mt-1 text-xs text-slate-500">{new Date(c.createdAt).toLocaleString('pt-BR')}</div>
                    <div className="mt-2 text-xs text-slate-700">
                      Líquido: <span className="font-semibold">{brl.format(c.result.net)}</span> • Bruto:{' '}
                      <span className="font-semibold">{brl.format(c.result.gross)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="h-9 rounded-lg bg-slate-100 px-3 text-sm text-slate-700 hover:bg-slate-200"
                      onClick={() => void exportPdf('Cálculo de Rescisão', c)}
                      type="button"
                    >
                      PDF
                    </button>
                    <button
                      className="h-9 rounded-lg bg-red-50 px-3 text-sm text-red-700 hover:bg-red-100"
                      onClick={() => openDeleteHistoryConfirm(c)}
                      type="button"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {historyQ.isLoading && <div className="text-sm text-slate-500">Carregando…</div>}
            {historyQ.isError && <div className="text-sm text-red-600">Falha ao carregar histórico.</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

